import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IExerciseQuestionType } from './exercises.interface';
import { UserService } from './user.service';
import { ExerciseService } from './exercise.service';
import { Answers } from './answers';
import { getDisplaySolution } from './utils/display.utils';

@Injectable({
  providedIn: 'root'
})
export class AnswerService {

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private exerciseService: ExerciseService
  ) { }

  public async submitAnswers(questions: IExerciseQuestionType[], assignmentId?: string): Promise<void> {
    const user = await this.userService.getUser();
    if (!user || !this.exerciseService.activeExercise) {
      console.error('Missing user or active exercise');
      return;
    }

    const transformedQuestions = questions.map(question => {
      const userAnswer = question.userAnswer || (question as any).currentSolution;
      
      if (!question._id || !question.solution || !userAnswer || question.isCorrect === undefined) {
        console.error('Invalid question data:', question);
        return null;
      }

      return {
        uid: user.uid,
        exerciseId: this.exerciseService.activeExercise!._id!,
        questionId: question._id,
        question: question.question,
        solution: question.solution,
        solutionString: getDisplaySolution(question),
        userAnswer: userAnswer,
        isCorrect: question.isCorrect
      };
    }).filter(q => q !== null);

    if (transformedQuestions.length === 0) {
      console.error('No valid questions to submit');
      return;
    }

    const payload = {
      questions: transformedQuestions,
      assignmentId
    };

    await this.http.post<IExerciseQuestionType[]>(`answers`, payload).toPromise();
  }

  public async getAnswersForUser(userId: string): Promise<Answers[]> {
    const response = await this.http.get<Answers[]>(`answers/user/${userId}`).toPromise();
    return response || [];
  }

  public async getAnswersForAssignment(assignmentId: string): Promise<Answers[]> {
    const response = await this.http.get<Answers[]>(`answers/assignment/${assignmentId}`).toPromise();
    return response || [];
  }

  /**
   * Get assignment progress and calculate completion percentage
   */
  public async getAssignmentProgress(assignmentId: string): Promise<{ progress: number, answeredQuestions: number, totalQuestions: number }> {
    try {
      const answers = await this.getAnswersForAssignment(assignmentId);
      const answeredQuestions = answers.length;
      
      // Get the assignment details directly from the API
      const user = await this.userService.getUser();
      if (!user) {
        return { progress: 0, answeredQuestions: 0, totalQuestions: 0 };
      }
      
      // Get assignments directly from API instead of using AssignmentService
      const assignmentsResponse = await this.http.get<{user: any, assignments: any[]}>(`user/assignments`).toPromise();
      const assignment = assignmentsResponse?.assignments?.find(a => a._id === assignmentId);
      const totalQuestions = assignment?.questionCount || 0;
      
      const progress = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
      
      return {
        progress: Math.round(progress),
        answeredQuestions,
        totalQuestions
      };
    } catch (error) {
      console.error('Error getting assignment progress:', error);
      return { progress: 0, answeredQuestions: 0, totalQuestions: 0 };
    }
  }
}
